import { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Divider, Paper, Snackbar, Switch,
  TextField, Typography,
} from '@mui/material';
import { ForwardToInbox } from '@mui/icons-material';
import {
  getNotificationSettings, updateNotificationSettings, sendTestEmail,
  type NotificationSettings, type NotifyEventKey, type TestEmailResult,
} from '../../../services/notificationSettingsService';

/**
 * Admin → Email Notifications. Every event ships OFF; the lab flips the ones
 * it wants and sets the recipient list. Client-audience events (e.g. "intake
 * returned") email the request's own contact, not this list.
 */
export const EmailNotificationsPage = () => {
  const [settings, setSettings] = useState<NotificationSettings | null>(null);
  const [recipientsText, setRecipientsText] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<TestEmailResult | null>(null);

  const load = useCallback(async () => {
    try {
      const s = await getNotificationSettings();
      setSettings(s);
      setRecipientsText(s.recipients.join(', '));
    } catch (err) {
      setError((err as Error).message || 'Failed to load notification settings.');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (key: NotifyEventKey, value: boolean) => {
    if (!settings) return;
    setSettings({ ...settings, events: { ...settings.events, [key]: value } });
    try {
      setSettings(await updateNotificationSettings({ events: { [key]: value } }));
      setSaved(true);
    } catch (err) {
      setError((err as Error).message || 'Failed to save.');
      load();
    }
  };

  const saveRecipients = async () => {
    try {
      const recipients = recipientsText.split(',').map((r) => r.trim()).filter(Boolean);
      const s = await updateNotificationSettings({ recipients });
      setSettings(s);
      setRecipientsText(s.recipients.join(', '));
      setSaved(true);
    } catch (err) {
      setError((err as Error).message || 'Failed to save recipients.');
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  }
  if (!settings) {
    return <Box sx={{ p: 3 }}><Alert severity="error">{error ?? 'Could not load settings.'}</Alert></Box>;
  }

  return (
    <Box sx={{ p: 3, maxWidth: 760 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>Email Notifications</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        All notifications are off by default. Enable the events the lab wants to hear about.
      </Typography>
      {!settings.delivery.configured && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>Email delivery is not configured on the server</strong> — enabled events are
          logged but no email is actually sent. Add a <code>SENDGRID_API_KEY</code> (and
          optionally <code>SENDER_EMAIL</code>) to the API app settings to turn delivery on,
          then use "Send test email" below to verify.
        </Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Paper variant="outlined" sx={{ mb: 3 }}>
        {settings.defs.map((def, i) => (
          <Box key={def.key}>
            {i > 0 && <Divider />}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{def.label}</Typography>
                  <Chip
                    label={def.audience === 'client'
                      ? 'emails the client'
                      : def.audience === 'creator'
                        ? 'emails only the link creator'
                        : 'emails the lab list'}
                    size="small"
                    sx={{ height: 18, fontSize: '0.65rem' }}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary">{def.description}</Typography>
              </Box>
              <Switch
                checked={settings.events[def.key]}
                onChange={(e) => toggle(def.key, e.target.checked)}
              />
            </Box>
          </Box>
        ))}
      </Paper>

      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Lab recipients</Typography>
      <TextField
        value={recipientsText}
        onChange={(e) => setRecipientsText(e.target.value)}
        onBlur={saveRecipients}
        helperText="Comma-separated emails (e.g. fredlab@bgcengineering.ca). Lab-audience events go here, plus contextual recipients like the intake link's creator. Saves on blur."
        fullWidth
        size="small"
        placeholder="fredlab@bgcengineering.ca"
      />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 3 }}>
        <Button
          variant="outlined" size="small" startIcon={<ForwardToInbox />}
          onClick={async () => {
            setTesting(true);
            setTestResult(null);
            try {
              setTestResult(await sendTestEmail());
            } catch (err) {
              setError((err as Error).message || 'Test email failed.');
            } finally {
              setTesting(false);
            }
          }}
          disabled={testing}
          sx={{ textTransform: 'none' }}
        >
          {testing ? 'Sending…' : 'Send test email'}
        </Button>
        <Typography variant="caption" color="text.secondary">
          Sends to you, bypassing the event toggles — verifies delivery end to end.
        </Typography>
      </Box>
      {testResult && (
        <Alert severity={testResult.emailSent ? 'success' : 'warning'} sx={{ mt: 1.5 }}>
          {testResult.emailSent
            ? `Test email sent to ${testResult.to.join(', ')} (from ${testResult.from}) — check the inbox.`
            : testResult.reason ?? 'The test email was not sent.'}
        </Alert>
      )}

      <Snackbar
        open={saved}
        autoHideDuration={1500}
        onClose={() => setSaved(false)}
        message="Saved"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default EmailNotificationsPage;
